import React, { useState } from 'react';
import { CalendarIcon } from '@heroicons/react/outline';

const SectionDivider = ({ title, color = 'blue' }) => {
  const colors = {
    blue: 'border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400',
    green: 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
    purple: 'border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400'
  };

  const colorClasses = colors[color] || colors.blue;

  return (
    <div className="relative my-8">
      <div className="absolute inset-0 flex items-center">
        <div className={`w-full border-t ${colorClasses.split(' ')[0]} ${colorClasses.split(' ')[1]}`}></div>
      </div>
      <div className="relative flex justify-center">
        <span className={`bg-white dark:bg-gray-800 px-4 text-lg font-semibold ${colorClasses.split(' ')[2]} ${colorClasses.split(' ')[3]}`}>
          {title}
        </span>
      </div>
    </div>
  );
};

const ToggleSwitch = ({ enabled, onChange, label, disabled }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      disabled={disabled}
    >
      <div
        className={`block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out ${
          enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      />
      <div
        className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
    <span className={`text-gray-700 dark:text-gray-200 ${disabled ? 'opacity-60' : ''}`}>{label}</span>
  </div>
);

const DateTimeInput = ({ value, onChange, label, disabled, min }) => {
  const inputRef = React.useRef(null);
  
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 16);
  };

  const handleDateChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div>
      <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="datetime-local"
          value={formatDateForInput(value)}
          onChange={handleDateChange}
          min={min}
          className={`input-base w-full pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 ${
            disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''
          }`}
          disabled={disabled}
          required
        />
        <div 
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${!disabled && 'cursor-pointer hover:text-blue-600'}`}
          onClick={() => !disabled && inputRef.current?.showPicker()}
        >
          <CalendarIcon className={`h-5 w-5 ${disabled ? 'text-gray-400' : 'text-blue-500'}`} />
        </div>
      </div>
    </div>
  );
};

const Scheduling = ({ data, updateData }) => {
  return (
    <div className="space-y-6">
      {/* قسم الجدولة والنشر */}
      <SectionDivider title="الجدولة والنشر" color="purple" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* البطاقة اليسرى - حالة النشر */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full order-1 md:order-2">
          <div className="space-y-6">
            {/* حالة النشر */}
            <div className="flex flex-col items-start">
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
                حالة النشر
              </label>
              <div className="flex items-center gap-6">
                <ToggleSwitch
                  enabled={data.isPublished}
                  onChange={(enabled) => updateData({ isPublished: enabled })}
                  label={data.isPublished ? 'منشور' : 'مجدول'}
                />
                
                {/* زر قريبًا */}
                <ToggleSwitch
                  enabled={data.isComingSoon}
                  onChange={(enabled) => updateData({ isComingSoon: enabled })}
                  label="قريبًا"
                  disabled={data.isPublished} // تعطيل الزر إذا كانت الدورة منشورة
                />
              </div>
              {data.isComingSoon && !data.isPublished && (
                <p className="text-xs text-gray-500 mt-2">سيظهر زر "قريبًا" بدلاً من زر الشراء وسيتم إخفاء السعر</p>
              )}
            </div>

            {/* تاريخ النشر */}
            <DateTimeInput
              label="تاريخ النشر"
              value={data.publishDate}
              onChange={(value) => {
                updateData({ 
                  publishDate: value,
                  startDate: !data.isPublished ? value : data.startDate 
                });
              }}
              disabled={data.isPublished}
            />
          </div>
        </div>

        {/* البطاقة اليمنى - مواعيد الدورة */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg h-full order-2 md:order-1">
          <div className="space-y-6">
            {/* بداية الدورة */}
            <DateTimeInput
              label="بداية الدورة"
              value={data.startDate}
              onChange={(value) => updateData({ startDate: value })}
              disabled={!data.isPublished && data.publishDate}
              min={!data.isPublished ? data.publishDate : undefined}
            />

            {/* نهاية الدورة */}
            <DateTimeInput
              label="نهاية الدورة"
              value={data.endDate}
              onChange={(value) => updateData({ endDate: value })}
              min={data.startDate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scheduling;
